import React, { useEffect, useState } from "react";
import "./ThankYou.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import HttpApis from "../../services/HttpApis";
import { Notyf } from "notyf";

export default function ThankYou() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  // Stripe round-trips this from the Payment Link query string.
  // It's our PlanPrice id — the backend uses it to link the Subscription row.
  const clientReferenceId = searchParams.get("client_reference_id");

  const [loading, setLoading] = useState(true);
  const [transactionId, setTransactionId] = useState("");
  const [receiptUrl, setReceiptUrl] = useState("");
  // Referral attribution (optional courtesy line shown only if the user
  // subscribed through a creator's referral link). Failure here never blocks
  // the rest of the success page.
  const [referredBy, setReferredBy] = useState(null);

  const notyf = new Notyf();

  useEffect(() => {
    const verifyPayment = async () => {
      console.log("👉 FULL URL:", window.location.href);

      if (!sessionId) {
        console.error("❌ No session_id found in URL");
        notyf.error("Invalid session");
        // No session_id means this isn't a completed checkout — most often
        // the user hit Stripe's back/cancel button. Don't fall through to the
        // "Payment Successful!" render; bounce them back to the account page.
        navigate("/accountPage", { replace: true });
        return;
      }

      const alreadyProcessed = localStorage.getItem(`payment_${sessionId}`);
      if (alreadyProcessed) {
        console.log("⚠️ Already processed:", sessionId);
        setTransactionId(sessionId);
        setLoading(false);
        return;
      }

      try {
        const payload = {
          platform: "stripe",
          session_id: sessionId,
          // Real PlanPrice id from Stripe's client_reference_id; falls back
          // to "" so backend treats it as legacy/unknown (still creates
          // a Subscription row, just without plan_id link).
          product_id: clientReferenceId || ""
        };

        console.log("📤 Sending to purchase API:", payload);

        const response = await HttpApis.purchase(payload);

        console.log("📥 Purchase API Response:", response);

        if (response?.data) {
          const receiptId = response.data.receipt_id;
          const receiptUrlFromApi = response.data.receipt_url;

          setTransactionId(receiptId || sessionId);
          setReceiptUrl(receiptUrlFromApi || "");

          // ✅ FIXED: correct nesting is response.data.data.subscription
          const subscriptionData = response.data.data?.subscription;

          if (subscriptionData) {
            console.log("📦 Subscription Data:", subscriptionData);

            // ✅ Store full subscription object for display
            localStorage.setItem(
              "subscriptionData",
              JSON.stringify(subscriptionData)
            );

            // ✅ Store subscription ID separately (used for cancellation)
            if (subscriptionData.id) {
              localStorage.setItem("subscriptionId", String(subscriptionData.id));
            }

            // ✅ Store transaction_id as receipt_id if needed
            if (subscriptionData.transaction_id) {
              localStorage.setItem("receipt_id", subscriptionData.transaction_id);
            }

            window.dispatchEvent(new Event("subscriptionUpdated"));
          }

          // GA4 purchase — the backend only sends ga4_purchase on the request
          // that first confirmed a paid charge, so refreshes/revisits, $0 trials
          // and failed verifications never reach this.
          const ga4 = response.data.ga4_purchase;
          if (ga4 && typeof window.gtag === "function") {
            window.gtag("event", "purchase", {
              transaction_id: ga4.transaction_id,
              value: ga4.value,
              currency: ga4.currency,
              items: [
                {
                  item_id: String(ga4.plan_price_id ?? ga4.plan_id ?? ""),
                  item_name: ga4.plan_name,
                  item_category: "subscription",
                  price: ga4.value,
                  quantity: 1,
                },
              ],
              ...(ga4.test ? { debug_mode: true } : {}),
            });
          }

          localStorage.setItem(`payment_${sessionId}`, "done");

          notyf.success("Payment verified successfully");
        }
      } catch (error) {
        console.error("❌ Purchase API Error:", error);
        notyf.error("Payment verification failed");
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  // Fetch the user's referral attribution once payment verification is done.
  // Purely cosmetic — failures are silent.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    HttpApis.getMyReferralAttribution()
      .then((resp) => {
        if (cancelled) return;
        const a = resp?.data?.data?.attribution || resp?.data?.attribution;
        if (a?.creator?.name) setReferredBy(a.creator);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loading]);

  if (loading) {
    return <div className="thankyou-container">Verifying payment...</div>;
  }

  return (
    <div className="thankyou-container">
      <div className="thankyou-card">
        <div className="check-icon">✓</div>

        <h1>Payment Successful!</h1>
        <p>Your payment was successfully processed.</p>

        {referredBy && (
          <div className="referred-by-box">
            {referredBy.profile_img ? (
              <img
                src={referredBy.profile_img}
                alt={referredBy.name}
                className="referred-by-avatar"
              />
            ) : (
              <div className="referred-by-avatar referred-by-avatar--placeholder">
                {String(referredBy.name).charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="referred-by-line">
                Subscribed through <strong>{referredBy.name}</strong>'s referral.
              </p>
              <p className="referred-by-sub">
                Now go enjoy their content — thanks for supporting a creator. 🎬
              </p>
            </div>
          </div>
        )}

        <div className="details-box">
          <p><strong>Transaction ID:</strong></p>
          <p>{transactionId}</p>
        </div>

        {receiptUrl && (
          <a
            href={receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              marginTop: "15px",
              color: "#ff9800",
              textDecoration: "underline",
              textAlign: "center"
            }}
          >
            View Receipt
          </a>
        )}

        <button className="go-home-btn" onClick={() => navigate("/home")}>
          Go to Home
        </button>
      </div>
    </div>
  );
}